import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, formatINR } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import {
  Plus, TrendUp, TrendDown, Trash, ChartLineUp, Coins, PencilSimple, Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = ["SIP", "Mutual Fund", "Stocks", "FD", "PPF", "Bonds", "Gold", "Other"];
const COLORS = ["#6366F1", "#0F172A", "#10B981", "#F59E0B", "#38BDF8", "#8B5CF6"];

const PRESETS = [
  { name: "Nifty 50 Index Fund", type: "SIP", monthly_contribution: "5000" },
  { name: "Flexi Cap Mutual Fund", type: "Mutual Fund", monthly_contribution: "5000" },
  { name: "PPF Account", type: "PPF" },
  { name: "Gold ETF", type: "Gold" },
  { name: "Fixed Deposit", type: "FD" },
  { name: "Debt / Liquid Fund", type: "Mutual Fund" },
];

const emptyForm = {
  name: "", type: "SIP", invested_amount: "", current_value: "", monthly_contribution: "", start_date: "",
};

export default function Investments() {
  const [items, setItems] = useState([]);
  const [pf, setPf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/investments"), api.get("/portfolio")]);
      setItems(a.data);
      setPf(b.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditId(null); setForm(emptyForm); setOpen(true); };
  const quickAdd = (preset) => {
    setEditId(null);
    setForm({ ...emptyForm, name: preset.name, type: preset.type,
      monthly_contribution: preset.monthly_contribution || "" });
    setOpen(true);
  };
  const openEdit = (inv) => {
    setEditId(inv.id);
    setForm({
      name: inv.name, type: inv.type,
      invested_amount: String(inv.invested_amount), current_value: String(inv.current_value),
      monthly_contribution: String(inv.monthly_contribution || ""), start_date: (inv.start_date || "").slice(0, 10),
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, type: form.type,
      invested_amount: Number(form.invested_amount) || 0,
      current_value: Number(form.current_value) || 0,
      monthly_contribution: Number(form.monthly_contribution) || 0,
      start_date: form.start_date || null,
    };
    if (editId) await api.put(`/investments/${editId}`, payload);
    else await api.post("/investments", payload);
    toast.success(editId ? "Investment updated" : "Investment added");
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/investments/${id}`);
    setItems((x) => x.filter((i) => i.id !== id));
    load();
    toast.success("Removed");
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Portfolio</h1>
          <p className="text-muted-foreground mt-1">Track your SIPs, mutual funds and other investments.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-investment-btn" onClick={openAdd} className="rounded-full bg-primary hover:bg-primary/90 gap-2">
              <Plus size={18} weight="bold" /> Add investment
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit" : "Add"} investment</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4 mt-2">
              <div>
                <Label>Name</Label>
                <Input data-testid="inv-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 h-11" placeholder="e.g. Nifty 50 Index Fund" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="inv-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Monthly SIP (₹)</Label>
                  <Input type="number" data-testid="inv-sip" value={form.monthly_contribution}
                    onChange={(e) => setForm({ ...form, monthly_contribution: e.target.value })}
                    className="mt-1.5 h-11 font-mono" placeholder="5000" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Invested (₹)</Label>
                  <Input type="number" data-testid="inv-invested" value={form.invested_amount}
                    onChange={(e) => setForm({ ...form, invested_amount: e.target.value })}
                    className="mt-1.5 h-11 font-mono" placeholder="60000" required />
                </div>
                <div>
                  <Label>Current value (₹)</Label>
                  <Input type="number" data-testid="inv-current" value={form.current_value}
                    onChange={(e) => setForm({ ...form, current_value: e.target.value })}
                    className="mt-1.5 h-11 font-mono" placeholder="72000" required />
                </div>
              </div>
              <div>
                <Label>Start date <span className="text-muted-foreground font-normal">optional</span></Label>
                <Input type="date" data-testid="inv-date" value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5 h-11" />
              </div>
              <Button type="submit" data-testid="inv-submit" className="w-full h-11 rounded-full bg-primary hover:bg-primary/90">
                {editId ? "Save changes" : "Add investment"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quick add presets */}
      <div className="mb-6" data-testid="quick-add-presets">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
          <Lightning size={14} weight="fill" className="text-indigo-500" /> Quick add
        </p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.name}
              data-testid={`preset-${p.name}`}
              onClick={() => quickAdd(p)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 transition-colors duration-200"
            >
              <Plus size={15} weight="bold" /> {p.name}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center soft-shadow">
          <div className="h-14 w-14 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
            <ChartLineUp size={26} weight="duotone" className="text-indigo-600" />
          </div>
          <p className="font-heading font-semibold text-lg text-slate-900">Add your investments</p>
          <p className="text-sm text-slate-500 mt-1.5 max-w-sm mx-auto leading-relaxed">
            Add your SIPs, mutual funds, FDs or gold in a few seconds to track growth here.
            It's private and quick — no bank login or account linking needed.
          </p>
          <Button data-testid="empty-add-investment-btn" onClick={openAdd}
            className="rounded-full bg-slate-900 hover:bg-slate-800 gap-2 mt-5">
            <Plus size={18} weight="bold" /> Add your first investment
          </Button>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { l: "Portfolio Value", v: formatINR(pf.total_value), sub: `${pf.count} holdings` },
              { l: "Invested", v: formatINR(pf.total_invested), sub: `₹${pf.monthly_sip.toLocaleString("en-IN")}/mo SIP` },
              { l: "Total Returns", v: formatINR(pf.total_returns), sub: `${pf.returns_pct}%`, gain: pf.total_returns >= 0 },
              { l: "Return %", v: `${pf.returns_pct}%`, sub: "overall", gain: pf.total_returns >= 0 },
            ].map((c, i) => (
              <motion.div key={c.l} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="rounded-xl border border-border bg-card p-5" data-testid={`pf-${c.l.toLowerCase().replace(/\s|%/g, "-")}`}>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">{c.l}</p>
                <p className={`font-mono font-semibold text-2xl tracking-tight ${c.gain === undefined ? "" : c.gain ? "text-primary" : "text-accent"}`}>{c.v}</p>
                <p className="text-xs text-muted-foreground mt-1">{c.sub}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 rounded-xl border border-border bg-card p-6">
              <h3 className="font-heading font-semibold text-lg mb-4">Growth over time</h3>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={pf.growth} margin={{ left: -10, right: 8, top: 8 }}>
                  <defs>
                    <linearGradient id="gVal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366F1" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#6366F1" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="gInv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#94A3B8" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#94A3B8" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Area type="monotone" dataKey="invested" name="Invested" stroke="#94A3B8" fill="url(#gInv)" strokeWidth={2} />
                  <Area type="monotone" dataKey="value" name="Value" stroke="#6366F1" fill="url(#gVal)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-heading font-semibold text-lg mb-4">Allocation</h3>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={pf.allocation} dataKey="value" nameKey="type" innerRadius={45} outerRadius={75} paddingAngle={2}>
                    {pf.allocation.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatINR(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {pf.allocation.map((a, i) => (
                  <div key={a.type} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{a.type}
                    </span>
                    <span className="font-mono">{formatINR(a.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Holdings */}
          <div className="rounded-xl border border-border bg-card divide-y divide-border overflow-hidden">
            {items.map((inv) => (
              <div key={inv.id} className="flex items-center gap-4 px-4 sm:px-5 py-4 hover:bg-secondary/30 transition-colors duration-200 group" data-testid="inv-row">
                <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                  <Coins size={18} weight="duotone" className="text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{inv.name}</p>
                  <p className="text-xs text-muted-foreground">{inv.type}{inv.monthly_contribution ? ` · ₹${inv.monthly_contribution.toLocaleString("en-IN")}/mo` : ""}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono font-semibold">{formatINR(inv.current_value)}</p>
                  <p className={`text-xs font-mono flex items-center gap-1 justify-end ${inv.returns >= 0 ? "text-primary" : "text-accent"}`}>
                    {inv.returns >= 0 ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
                    {inv.returns >= 0 ? "+" : ""}{formatINR(inv.returns)} ({inv.returns_pct}%)
                  </p>
                </div>
                <div className="flex gap-1 text-slate-400">
                  <button onClick={() => openEdit(inv)} data-testid={`edit-inv-${inv.id}`} className="hover:text-indigo-600 p-1 transition-colors duration-200"><PencilSimple size={18} /></button>
                  <button onClick={() => remove(inv.id)} data-testid={`delete-inv-${inv.id}`} className="hover:text-red-500 p-1 transition-colors duration-200"><Trash size={18} /></button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
